import React, { useEffect, useMemo, useState } from 'react'
import { message, Modal, PageHeader } from 'antd'
import { RouteComponentProps } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import Text from 'antd/lib/typography/Text'

import { ApplicationState } from 'src/store'
import { ButtonTitled } from 'src/components/Ant'
import { getFullRequest } from 'src/core/store/ducks/requests/actions'
import {
  getIssuances,
  sendIssuance,
  setTicketsEmission
} from 'src/core/store/ducks/issuances/actions'
import { useT } from 'src/core/hooks/use-translation'
import SegmentControl, { Segments } from 'src/components/SegmentControl'

import { ContainerSegmentControl, WrapOptionCancel, WrapSpin } from './styles'
import Airs from './components/airs'
import Hotels from './components/hotels'
import Vehicles from './components/vehicles'

interface Params {
  request_agency_id: string
  request_id: string
}

type SegmentKeys = 'airs' | 'vehicle' | 'hotels'

type SummarySegments = Segments<SegmentKeys>

export default function IssuancesSummary({
  history,
  match: {
    params: { request_agency_id, request_id }
  }
}: RouteComponentProps<Params>) {
  const { t } = useT()
  const dispatch = useDispatch()
  const { issuances, requests } = useSelector((state: ApplicationState) => ({
    issuances: state.issuances,
    requests: state.requests
  }))

  const [changingStatus, setChangingStatus] = useState<boolean>(false)
  const canEdit = useMemo(
    () =>
      issuances.list.length && issuances.list[0].status === 'EMISSION_PENDING',
    [issuances.list]
  )
  const [current, setCurrent] = useState<SegmentKeys>('airs')

  const segments = useMemo<SummarySegments>(
    () => ({
      airs: {
        disabled: false,
        title: 'web.services.airs.title',
        renderContent: () => (
          <Airs canEdit={canEdit} request={requests.fullRequest} />
        )
      },
      hotels: {
        disabled: false,
        title: 'web.services.hotels.title',
        renderContent: () => (
          <Hotels canEdit={canEdit} request={requests.fullRequest} />
        )
      },
      vehicle: {
        disabled: false,
        title: 'web.services.vehicle.title',
        renderContent: () => (
          <Vehicles canEdit={canEdit} request={requests.fullRequest} />
        )
      }
    }),
    [requests.fullRequest, canEdit]
  )

  useEffect(() => {
    dispatch(getFullRequest(request_id, 'issuances'))
    dispatch(
      getIssuances({
        filterOrganizationsId: '',
        filterStatus: [],
        filterTenantId: '',
        request_agency_ids: [request_agency_id],
        request_ids: [request_id],
        request_numbers: '',
        request_title: ''
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request_id])

  useEffect(() => {
    if (issuances.sendIssuanceLoading !== changingStatus) {
      setChangingStatus(issuances.sendIssuanceLoading)

      if (!issuances.sendIssuanceLoading) {
        if (issuances.sendIssuanceError) {
          message.error(issuances.sendIssuanceError, 5)
        } else {
          message.success(
            t('common.push-notifications.issuances-send-successful'),
            5
          )
          history.push('/agencies/issuances')
        }

        Modal.destroyAll()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issuances.sendIssuanceLoading])

  useEffect(() => {
    if (requests.fullRequest) {
      dispatch(
        setTicketsEmission(
          requests.fullRequest.request_tickets
            .filter(ticketItem =>
              ['AIR', 'VEHICLE', 'HOTEL'].includes(
                ticketItem.request_ticket_type
              )
            )
            .map(ticketItem => {
              const tickedFinder = issuances.ticketsEmission.find(
                ticket =>
                  ticket.request_ticket_id === ticketItem.request_ticket_id
              )

              return {
                request_ticket: ticketItem,
                pending: tickedFinder
                  ? tickedFinder?.pending
                  : ticketItem.status === 'EMISSION_PENDING',
                status: tickedFinder?.status,
                request_ticket_id: ticketItem.request_ticket_id,
                request_ticket_type: ticketItem.request_ticket_type,
                eticket_code:
                  ticketItem.request_ticket_air_selected?.eticket_code ||
                  ticketItem.request_ticket_hotel_selected?.eticket_code ||
                  ticketItem.request_ticket_vehicle_selected?.eticket_code
              }
            })
        )
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requests.fullRequest])

  function handleAction() {
    const ticketsError = issuances.ticketsEmission.find(
      item => item.pending && !item.eticket_code
    )

    if (ticketsError) {
      Modal.error({
        title: t('web.services.error-incomplete-registration')
      })
      return
    }

    Modal.confirm({
      cancelText: t('common.actions.cancel'),
      content: t('web.agencies.issuances.modal-send-issuance'),
      okText: t('common.actions.yes'),
      onCancel() {},
      onOk: async () => {
        const hasPendingIssues = issuances.ticketsEmission.some(
          ({ pending }) => pending
        )
        return new Promise(() => {
          dispatch(
            sendIssuance(
              issuances.list[0].request_agency_user_data.map(user_data => ({
                request_agency_id: issuances.list[0].request_agency_id,
                request_agency_emission_id:
                  user_data.request_agency_emission.request_agency_emission_id,
                status: hasPendingIssues ? 'APPROVED' : 'REPROVED'
              }))
            )
          )
        }).catch(() => {})
      },
      title: t('common.are-you-sure')
    })
  }

  if (!requests.fullRequestLoading && !requests.fullRequest) {
    return (
      <>
        <PageHeader
          ghost={false}
          onBack={() => history.goBack()}
          title="# - "
        />
        <WrapOptionCancel>
          <Text>{t('common.response.status-500')}</Text>
        </WrapOptionCancel>
      </>
    )
  }

  if (
    requests.fullRequestLoading ||
    !requests.fullRequest ||
    issuances.loading ||
    !issuances.list.length
  )
    return <WrapSpin />

  return (
    <>
      <PageHeader
        extra={[
          ...(canEdit
            ? [
                <ButtonTitled
                  icon="check"
                  key="btnSendIssuance"
                  onClick={handleAction}
                  size="default"
                  tooltip="web.agencies.issuances.form.btn-send-issuance"
                />
              ]
            : [])
        ]}
        ghost={false}
        onBack={() => history.goBack()}
        title={
          <>{`#${requests.fullRequest.request_number} - ${requests.fullRequest.title}`}</>
        }
      />
      <ContainerSegmentControl>
        <SegmentControl
          defaultValue="summary"
          onChange={({ target: { value } }) => {
            setCurrent(value)
          }}
          segments={segments}
          value={current}
        />
      </ContainerSegmentControl>
      {segments[current].renderContent()}
    </>
  )
}
